'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { Check, ChevronsUpDown } from 'lucide-react';

import type { HouseholdSwitcherMembership } from './HouseholdSwitcher';

export type HouseholdSwitcherSidebarProps = {
  readonly activeHouseholdId: string | null;
  readonly memberHouseholds: readonly HouseholdSwitcherMembership[];
  readonly onSelect: (householdId: string) => void;
  readonly disabled?: boolean;
};

export function HouseholdSwitcherSidebar({
  activeHouseholdId,
  memberHouseholds,
  onSelect,
  disabled,
}: HouseholdSwitcherSidebarProps) {
  if (memberHouseholds.length <= 1) {
    return null;
  }

  const active = memberHouseholds.find((h) => h.id === activeHouseholdId) ?? memberHouseholds[0];
  const activeName = active?.name ?? '';

  const trigger = (
    <div
      data-testid="household-switcher-trigger"
      aria-label="Switch household"
      role="button"
      className={cn(
        'mx-4 mt-6 mb-4 box-border block w-[calc(100%-2rem)] rounded border border-border px-4 py-2.5 transition-colors',
        disabled ? 'cursor-default' : 'cursor-pointer hover:bg-component-hover',
      )}
    >
      <div className="flex flex-row items-center gap-2">
        <div className="min-w-0 flex-1">
          <Text variant="overline" color="secondary" className="block">
            Viewing Household
          </Text>
          <Text variant="subtitle-sm" className="block truncate">
            {activeName}
          </Text>
        </div>
        <ChevronsUpDown size={16} className="text-secondary" />
      </div>
    </div>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={trigger} disabled={disabled} />
      <DropdownMenuContent align="start" positionerClassName="z-[1250]" className="w-[240px]">
        {memberHouseholds.map((household) => {
          const isActive = household.id === active?.id;
          return (
            <DropdownMenuItem
              key={household.id}
              onClick={() => {
                if (!isActive) {
                  onSelect(household.id);
                }
              }}
              data-testid={`household-switcher-option-${household.id}`}
            >
              <span className="flex-1">{household.name}</span>
              {isActive ? <Check className="size-4 text-secondary" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
